import { Injectable, Injector } from '@angular/core';

export interface CacheConfig {
  ttl: number;
  maxSize: number;
  storage: 'memory' | 'localStorage' | 'both';
  autoRefresh: boolean;
  refreshInterval: number;
  namespace: string;
}

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  key: string;
  namespace: string;
  metadata?: any;
}

export interface CacheStats {
  hits: number;
  misses: number;
  evictions: number;
  size: number;
  namespaces: string[];
}

export interface FetchOptions {
  forceRefresh?: boolean;
  backgroundRefresh?: boolean;
  blockUI?: boolean;
  silent?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CacheManagementService {
  private readonly DEFAULT_TTL = 15 * 60 * 1000;
  private readonly DEFAULT_MAX_SIZE = 50;
  
  private memoryCache = new Map<string, CacheEntry<any>>();
  private accessOrder = new Map<string, string[]>();
  private configs = new Map<string, CacheConfig>();
  private inFlightRequests = new Map<string, Promise<any>>();
  private autoRefreshTimers = new Map<string, any>();
  
  private stats = new Map<string, CacheStats>();

  private injector: Injector | null = null;

  constructor() {
    this.initializeDefaultConfigs();
  }

  setInjector(injector: Injector): void {
    this.injector = injector;
  }

  private initializeDefaultConfigs(): void {
    const defaultConfig: CacheConfig = {
      ttl: this.DEFAULT_TTL,
      maxSize: this.DEFAULT_MAX_SIZE,
      storage: 'memory',
      autoRefresh: false,
      refreshInterval: this.DEFAULT_TTL,
      namespace: 'default'
    };

    this.configs.set('subscription-date', {
      ...defaultConfig,
      namespace: 'subscription-date',
      maxSize: 10,
      storage: 'both',
      autoRefresh: true
    });

    this.configs.set('daily-menu', {
      ...defaultConfig,
      namespace: 'daily-menu',
      maxSize: 50,
      storage: 'both',
      autoRefresh: false
    });

    this.configs.set('data-service', {
      ...defaultConfig,
      namespace: 'data-service',
      maxSize: 50,
      storage: 'memory',
      autoRefresh: true
    });

    ['subscription-date', 'daily-menu', 'data-service'].forEach(ns => {
      this.stats.set(ns, {
        hits: 0,
        misses: 0,
        evictions: 0,
        size: 0,
        namespaces: [ns]
      });
      this.accessOrder.set(ns, []);
    });
  }

  setConfig(namespace: string, config: Partial<CacheConfig>): void {
    const existing = this.configs.get(namespace) || {
      ttl: this.DEFAULT_TTL,
      maxSize: this.DEFAULT_MAX_SIZE,
      storage: 'memory' as const,
      autoRefresh: false,
      refreshInterval: this.DEFAULT_TTL,
      namespace
    };
    
    this.configs.set(namespace, { ...existing, ...config });
    
    if (!this.stats.has(namespace)) {
      this.stats.set(namespace, {
        hits: 0,
        misses: 0,
        evictions: 0,
        size: 0,
        namespaces: [namespace]
      });
    }
    
    if (!this.accessOrder.has(namespace)) {
      this.accessOrder.set(namespace, []);
    }
  }

  getConfig(namespace: string): CacheConfig {
    return this.configs.get(namespace) || {
      ttl: this.DEFAULT_TTL,
      maxSize: this.DEFAULT_MAX_SIZE,
      storage: 'memory',
      autoRefresh: false,
      refreshInterval: this.DEFAULT_TTL,
      namespace
    };
  }

  get<T>(key: string, namespace: string): T | null {
    const fullKey = this.getFullKey(key, namespace);
    const config = this.getConfig(namespace);
    
    let entry = this.memoryCache.get(fullKey);
    
    if (!entry && (config.storage === 'localStorage' || config.storage === 'both')) {
      entry = this.getFromLocalStorage<T>(fullKey);
      if (entry) {
        this.memoryCache.set(fullKey, entry);
      }
    }
    
    if (!entry) {
      this.incrementStat(namespace, 'misses');
      return null;
    }
    
    if (!this.isCacheValid(entry, config)) {
      this.delete(key, namespace);
      this.incrementStat(namespace, 'misses');
      return null;
    }
    
    this.updateAccessOrder(namespace, fullKey);
    this.incrementStat(namespace, 'hits');
    
    return entry.data;
  }

  set<T>(key: string, data: T, namespace: string, metadata?: any): void {
    const fullKey = this.getFullKey(key, namespace);
    const config = this.getConfig(namespace);
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      key,
      namespace,
      metadata
    };
    
    this.memoryCache.set(fullKey, entry);
    
    if (config.storage === 'localStorage' || config.storage === 'both') {
      this.saveToLocalStorage(fullKey, entry);
    }
    
    this.updateAccessOrder(namespace, fullKey);
    this.evictIfNeeded(namespace);
    this.updateStat(namespace, 'size', this.getNamespaceSize(namespace));
  }

  has(key: string, namespace: string): boolean {
    return this.get(key, namespace) !== null;
  }

  delete(key: string, namespace: string): void {
    const fullKey = this.getFullKey(key, namespace);
    this.memoryCache.delete(fullKey);
    
    const config = this.getConfig(namespace);
    if (config.storage === 'localStorage' || config.storage === 'both') {
      this.removeFromLocalStorage(fullKey);
    }
    
    const order = this.accessOrder.get(namespace) || [];
    const index = order.indexOf(fullKey);
    if (index > -1) {
      order.splice(index, 1);
    }
    
    this.updateStat(namespace, 'size', this.getNamespaceSize(namespace));
  }

  clear(namespace?: string): void {
    if (namespace) {
      const prefix = `${namespace}:`;
      const keysToDelete: string[] = [];
      
      this.memoryCache.forEach((_, key) => {
        if (key.startsWith(prefix)) {
          keysToDelete.push(key);
        }
      });
      
      keysToDelete.forEach(key => {
        this.memoryCache.delete(key);
        this.removeFromLocalStorage(key);
      });
      
      this.accessOrder.set(namespace, []);
      this.updateStat(namespace, 'size', 0);
    } else {
      this.memoryCache.clear();
      this.accessOrder.clear();
      this.clearAllLocalStorage();
      
      this.stats.forEach((stat, ns) => {
        this.updateStat(ns, 'size', 0);
      });
    }
  }

  clearAllUserData(): void {
    console.log('[CacheManagement] Clearing all user data cache');
    this.clear('subscription-date');
    this.clear('daily-menu');
    this.clear('data-service');
    this.stopAllAutoRefresh();
    
    if (this.injector) {
      try {
        // const { CustomerPreferencesService } = require('./customer-preferences.service');
        // const prefsService = this.injector.get(CustomerPreferencesService);
        // prefsService.clearPreferences();
      } catch (e) {}
    }
  }

  private stopAllAutoRefresh(): void {
    this.autoRefreshTimers.forEach((timer, namespace) => {
      clearInterval(timer);
      console.log(`[CacheManagement] Stopped auto-refresh for ${namespace}`);
    });
    this.autoRefreshTimers.clear();
  }

  async getOrFetch<T>(
    key: string,
    namespace: string,
    fetchFn: () => Promise<T>,
    options?: FetchOptions
  ): Promise<T> {
    const cached = this.get<T>(key, namespace);
    
    if (cached && !options?.forceRefresh) {
      if (options?.backgroundRefresh) {
        this.triggerBackgroundRefresh(key, namespace, fetchFn);
      }
      return cached;
    }
    
    const requestKey = `${namespace}:${key}`;
    
    if (this.inFlightRequests.has(requestKey)) {
      return this.inFlightRequests.get(requestKey);
    }
    
    const promise = fetchFn()
      .then(data => {
        this.set(key, data, namespace);
        this.inFlightRequests.delete(requestKey);
        return data;
      })
      .catch(err => {
        this.inFlightRequests.delete(requestKey);
        if (!options?.silent) {
          throw err;
        }
        return null as T;
      });
    
    this.inFlightRequests.set(requestKey, promise);
    return promise;
  }

  invalidatePattern(pattern: RegExp, namespace: string): void {
    const prefix = `${namespace}:`;
    const keysToDelete: string[] = [];
    
    this.memoryCache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        const actualKey = key.substring(prefix.length);
        if (pattern.test(actualKey)) {
          keysToDelete.push(key);
        }
      }
    });
    
    keysToDelete.forEach(key => {
      this.memoryCache.delete(key);
      this.removeFromLocalStorage(key);
      
      const order = this.accessOrder.get(namespace) || [];
      const index = order.indexOf(key);
      if (index > -1) {
        order.splice(index, 1);
      }
    });
    
    this.updateStat(namespace, 'size', this.getNamespaceSize(namespace));
  }

  startAutoRefresh(namespace: string, refreshFn: () => Promise<void>): void {
    this.stopAutoRefresh(namespace);
    
    const config = this.getConfig(namespace);
    
    const timer = setInterval(async () => {
      console.log(`[CacheManagement] Auto-refresh triggered for ${namespace}`);
      try {
        await refreshFn();
      } catch (error) {
        console.error(`[CacheManagement] Auto-refresh failed for ${namespace}:`, error);
      }
    }, config.refreshInterval);
    
    this.autoRefreshTimers.set(namespace, timer);
    console.log(`[CacheManagement] Auto-refresh timer started for ${namespace} (${config.refreshInterval}ms)`);
  }

  stopAutoRefresh(namespace: string): void {
    const timer = this.autoRefreshTimers.get(namespace);
    if (timer) {
      clearInterval(timer);
      this.autoRefreshTimers.delete(namespace);
      console.log(`[CacheManagement] Auto-refresh timer stopped for ${namespace}`);
    }
  }

  getStats(namespace?: string): CacheStats {
    if (namespace) {
      return this.stats.get(namespace) || {
        hits: 0,
        misses: 0,
        evictions: 0,
        size: 0,
        namespaces: [namespace]
      };
    }
    
    const combined: CacheStats = {
      hits: 0,
      misses: 0,
      evictions: 0,
      size: 0,
      namespaces: Array.from(this.stats.keys())
    };
    
    this.stats.forEach(stat => {
      combined.hits += stat.hits;
      combined.misses += stat.misses;
      combined.evictions += stat.evictions;
      combined.size += stat.size;
    });
    
    return combined;
  }

  clearStats(): void {
    this.stats.forEach((_, namespace) => {
      this.stats.set(namespace, {
        hits: 0,
        misses: 0,
        evictions: 0,
        size: this.getNamespaceSize(namespace),
        namespaces: [namespace]
      });
    });
  }

  private triggerBackgroundRefresh<T>(
    key: string,
    namespace: string,
    fetchFn: () => Promise<T>
  ): void {
    fetchFn()
      .then(data => {
        this.set(key, data, namespace);
        console.log(`[CacheManagement] Background refresh completed for ${namespace}:${key}`);
      })
      .catch(err => {
        console.warn(`[CacheManagement] Background refresh failed for ${namespace}:${key}:`, err);
      });
  }

  private isCacheValid(entry: CacheEntry<any>, config: CacheConfig): boolean {
    const age = Date.now() - entry.timestamp;
    return age < config.ttl;
  }

  private evictIfNeeded(namespace: string): void {
    const config = this.getConfig(namespace);
    const order = this.accessOrder.get(namespace) || [];
    
    while (order.length > config.maxSize) {
      const oldestKey = order.shift();
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
        this.removeFromLocalStorage(oldestKey);
        this.incrementStat(namespace, 'evictions');
      }
    }
  }

  private updateAccessOrder(namespace: string, fullKey: string): void {
    const order = this.accessOrder.get(namespace) || [];
    const index = order.indexOf(fullKey);
    
    if (index > -1) {
      order.splice(index, 1);
    }
    
    order.push(fullKey);
    this.accessOrder.set(namespace, order);
  }

  private getNamespaceSize(namespace: string): number {
    const prefix = `${namespace}:`;
    let count = 0;
    
    this.memoryCache.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        count++;
      }
    });
    
    return count;
  }

  private getFullKey(key: string, namespace: string): string {
    return `${namespace}:${key}`;
  }

  private getFromLocalStorage<T>(fullKey: string): CacheEntry<T> | null {
    try {
      const item = localStorage.getItem(fullKey);
      if (item) {
        return JSON.parse(item);
      }
    } catch (e) {
      console.warn(`[CacheManagement] Error reading from localStorage for ${fullKey}:`, e);
    }
    return null;
  }

  private saveToLocalStorage<T>(fullKey: string, entry: CacheEntry<T>): void {
    try {
      localStorage.setItem(fullKey, JSON.stringify(entry));
    } catch (e) {
      console.warn(`[CacheManagement] Error saving to localStorage for ${fullKey}:`, e);
    }
  }

  private removeFromLocalStorage(fullKey: string): void {
    try {
      localStorage.removeItem(fullKey);
    } catch (e) {
      console.warn(`[CacheManagement] Error removing from localStorage for ${fullKey}:`, e);
    }
  }

  private clearAllLocalStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('subscription-date:') || key.startsWith('daily-menu:') || key.startsWith('data-service:'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.warn('[CacheManagement] Error clearing localStorage:', e);
    }
  }

  private incrementStat(namespace: string, stat: keyof CacheStats): void {
    const stats = this.stats.get(namespace);
    if (stats && typeof stats[stat] === 'number') {
      (stats[stat] as number)++;
    }
  }

  private updateStat(namespace: string, stat: keyof CacheStats, value: number): void {
    const stats = this.stats.get(namespace);
    if (stats) {
      (stats[stat] as number) = value;
    }
  }
}
